import React, { Suspense, useEffect, useState, useRef } from 'react';
import { useParams, useHistory, Link, Prompt } from 'react-router-dom';
import {
  Button,
  Flex,
  message,
  Spin,
  Typography,
  Form,
  Input,
  Select,
  Upload as AntUpload,
  Space,
  Segmented,
  Tag,
  DatePicker,
  Checkbox,
  Divider,
  InputNumber,
  Tooltip,
  Switch,
  Modal,
  Steps,
  Descriptions
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { Card, CardBody, Container } from 'reactstrap';
import { Col, Row } from 'react-bootstrap';
import Palette from '../../../utils/Palette';
import Iconify from '../../reusable/Iconify';
import swal from '../../reusable/CustomSweetAlert';
import Upload from 'models/UploadModel';
import Placeholder from 'utils/Placeholder';
import dayjs from 'dayjs';
import Helper from 'utils/Helper';
import Grant from 'models/GrantModel';
import PDFModal from './PDFModal';

const stepItems = [
  {
    title: "Application Info",
  },
  {
    title: "Documents & Account Information",
  },
  {
    title: "Translated Work & Invoice",
  },
  {
    title: "Grant Completed",
  },
];

const grantStatusToStatusStepMap = {
  'FORM_WAITING': 0,
  'FORM_APPROVED': 0,
  'DOCUMENT_WAITING': 1,
  'DOCUMENT_REJECTED': 1,
  'DOCUMENT_APPROVED': 1,
  'PAYMENT_WAITING': 2,
  'PAYMENT_REJECTED': 2,
  'PAYMENT_APPROVED': 3,
}

function StepTitle({ title, isActive }) {
  return (
    <Tooltip
      title={title}
      placement="bottomLeft"
    >
      {title}
    </Tooltip>
  )
}
function StepDescription({ message }) {
  return (
    <Typography.Text style={{ fontSize: 11 }}>
      {message}
    </Typography.Text>
  )
}
const StatusSteps = ({ grant, currentStep, onChangeCurrentStep }) => {

  const status = grant?.status || 'FORM_WAITING';
  const statusStep = grantStatusToStatusStepMap[status];

  const statusStepItems = stepItems.map((item, index) => {
    const status = index < statusStep
      ? 'finish'
      : statusStep == index
        ? 'process'
        : 'wait';

    const isActive = currentStep == index

    return {
      status,
      title: <StepTitle title={item.title} />,
    }
  })

  const handleChange = (value) => {
    if (onChangeCurrentStep) onChangeCurrentStep(value);
  }

  return (
    <Steps
      type="navigation"
      labelPlacement="vertical"
      size="small"
      current={currentStep}
      onChange={handleChange}
      items={statusStepItems}
    />
  )
}

const getFileNameFromUrl = (url) => {
  if (typeof url !== 'string') return '';

  let fileName = url.split("/").at(-1);
  fileName = fileName.slice(fileName.indexOf("-") + 1);
  fileName = decodeURIComponent(fileName);

  return fileName;
}

const GrantDocumentLink = ({ grant, documentType }) => {
  let data = grant[documentType];

  if (!data) {
    return (
      <Typography.Text>
        -
      </Typography.Text>
    )
  }

  let url = Grant.getViewUrlById(grant?.id, documentType);

  const fileName = getFileNameFromUrl(data);

  return (
    <Typography.Link href={url} target="_blank">
      {fileName}
      <div style={{ position: 'relative', display: 'inline-block', marginLeft: '4px', top: '-2px' }}>
        <Iconify icon={"mdi:open-in-new"} width={16} height={16} />
      </div>
    </Typography.Link>
  )
}

const GrantDocumentPDFButton = ({ data, documentType, onOpenPDF }) => {

  if (!data) return (
    <Typography.Text>
      -
    </Typography.Text>
  )

  const handleClick = () => {
    if (onOpenPDF) onOpenPDF(documentType);
  }

  return (
    <Button onClick={handleClick}>
      Preview PDF
    </Button>
  )
}

const statusStepToDescriptionFieldsMap = {
  0: [
    { name: "first_name", renderData: (data) => data?.members?.first_name },
    { name: "last_name", renderData: (data) => data?.members?.last_name },
    { name: "book_title_id" },
    { name: "book_title_en" },
    { name: "book_author" },
    { name: "book_publisher" },
    { name: "book_target_language" },
    { name: "number_of_page", renderData: (data) => data?.book_page },
    { name: "book_genre" },
    { name: "applicant_firstname" },
    { name: "applicant_lastname" },
    { name: "applicant_phone_number" },
    { name: "applicant_email" },
    { name: "publisher_name" },
    { name: "publisher_address" },
    { name: "publisher_phone_number" },
  ],
  1: [
    {
      name: "document_copyright_publisher_contract",
      renderChildren: (data, name, grant, onOpenPDF) => (
        <GrantDocumentPDFButton
          data={data}
          documentType={name}
          onOpenPDF={onOpenPDF}
        />
      )
    },
    {
      name: "document_translator_publisher_contract",
      renderChildren: (data, name, grant, onOpenPDF) => (
        <GrantDocumentPDFButton
          data={data}
          documentType={name}
          onOpenPDF={onOpenPDF}
        />
      )
    },
    {
      name: "document_translator_cv",
      renderChildren: (data, name, grant, onOpenPDF) => (
        <GrantDocumentPDFButton
          data={data}
          documentType={name}
          onOpenPDF={onOpenPDF}
        />
      )
    },
    {
      name: "document_sample_translation",
      renderChildren: (data, name, grant, onOpenPDF) => (
        <GrantDocumentPDFButton
          data={data}
          documentType={name}
          onOpenPDF={onOpenPDF}
        />
      )
    },
    {
      name: "document_original_manuscript",
      renderChildren: (data, name, grant, onOpenPDF) => (
        <GrantDocumentPDFButton
          data={data}
          documentType={name}
          onOpenPDF={onOpenPDF}
        />
      )
    },
    { name: "bank_name" },
    { name: "bank_account_name" },
    { name: "bank_account_number" },
    { name: "bank_address" },
    { name: "bank_swift_code" },
    { name: "bank_iban" },
    { name: "bank_phone" },
    { name: "bank_email" },
  ],
  2: [
    {
      name: "payment_final_book",
      renderChildren: (data, name, grant, onOpenPDF) => (
        <GrantDocumentPDFButton
          data={data}
          documentType={name}
          onOpenPDF={onOpenPDF}
        />
      )
    },
    {
      name: "payment_invoice",
      renderChildren: (data, name, grant, onOpenPDF) => (
        <GrantDocumentPDFButton
          data={data}
          documentType={name}
          onOpenPDF={onOpenPDF}
        />
      )
    },
  ],
}

const formatField = (field) => {
  return field.split("_").map(w => w.at(0).toUpperCase().concat(
    w.slice(1).toLowerCase()
  )).join(" ");
}

const StepDescriptions = ({ currentStep, grant }) => {
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);

  const handleOpenPDFModal = (type) => {
    setOpenPDFModal(true);
    setSelectedDocumentType(type);
  }

  const handleClosePDFModal = () => {
    setOpenPDFModal(false);
    setSelectedDocumentType(null);
  }

  const { title } = stepItems[currentStep] || {};

  const displayedFields = statusStepToDescriptionFieldsMap[currentStep] || [];

  const getDescriptionItems = () => {
    return displayedFields.map((field, index) => {
      const { name, renderData, renderChildren } = field;

      let data = grant[name];
      if (!!renderData) {
        data = renderData(grant);
      }

      const getChildren = () => {
        if (!!renderChildren) return renderChildren(data, name, grant, handleOpenPDFModal);

        return data || '-';
      }

      return (
        <Descriptions.Item key={name} label={formatField(name)} span={3}>
          <div style={{ flex: 1, marginTop: '-8px' }}>
            {getChildren()}
          </div>
        </Descriptions.Item>
      )
    })
  }

  return (
    <>
      <Descriptions title={title} layout="vertical">
        {getDescriptionItems()}
      </Descriptions>

      <PDFModal
        open={openPDFModal}
        handleClose={handleClosePDFModal}
        selectedGrant={grant}
        grantDocumentType={selectedDocumentType}
      />
    </>
  )
}

const statusStepToActionConfigsMap = {
  0: [
    {
      name: 'reject',
      type: "text",
      text: "Reject",
      confirmMessage: "This will reject the grant application, and notify the applicant of this rejection via email.",
      callback: async (grant, formData) => {
        try {
          await Grant.rejectGrant(grant.id, formData);
          message.success("Grant application rejected");
        } catch (e) {
          message.error("There was error from server");
        }
      },
    },
    {
      name: 'approve_application',
      type: "primary",
      text: "Approve",
      confirmMessage: "This will approve the grant application, and notify the applicant of this approval via email.",
      callback: async (grant) => {
        try {
          await Grant.approveGrant(grant.id);
          message.success("Grant application approved");
        } catch (e) {
          message.error("There was error from server");
        }
      },
    },
  ],
  1: [
    {
      name: 'report',
      type: "text",
      text: "Report Missing / Incorrect Field or Document",
      confirmMessage: "This will report the selected incorrect data as needs to be revised, along with the reason if included, and notify the applicant of this report via email",
      callback: async (grant, formData) => {
        try {
          await Grant.rejectGrantDocument(grant.id, formData);
          message.success("Grant application rejected");
        } catch (e) {
          message.error("There was error from server");
        }
      },
    },
    {
      name: 'approve_document',
      type: "primary",
      text: "Approve",
      confirmMessage: "This will approve the submitted document and account information, and notify the applicant of this approval via email.",
      callback: async (grant) => {
        try {
          await Grant.approveGrantDocument(grant.id);
          message.success("Grant application approved");
        } catch (e) {
          message.error("There was error from server");
        }
      },
    },
  ],
  2: [
    {
      name: 'report',
      type: "text",
      text: "Report Missing\\Incorrect Field or Document",
      confirmMessage: "This will report the selected incorrect data as incorrect \\ missing \\ needs revision along with the reason, if included, and notify the applicant of this report via email",
      callback: async (grant, formData) => {
        try {
          await Grant.rejectGrantPayment(grant.id, formData);
          message.success("Grant application rejected");
        } catch (e) {
          message.error("There was error from server");
        }
      },
    },
    {
      name: 'approve_final',
      type: "primary",
      text: "Approve",
      confirmMessage: "This will approve the submitted final translation work and payment invoice, and notify the applicant of this approval via email.",
      callback: async (grant) => {
        try {
          await Grant.approveGrantPayment(grant.id);
          message.success("Grant application approved");
        } catch (e) {
          message.error("There was error from server");
        }
      },
    },
  ],
}

const RejectReasonModal = ({ open, onClose, onConfirm, ...props }) => {
  const [form] = useForm();

  const handleClose = () => {
    if (onClose) onClose();
  }

  const handleConfirm = () => {
    const formData = form?.getFieldsValue();
    if (onConfirm) onConfirm(formData);
  }

  return (
    <Modal
      {...props}
      title="Reject Reason"
      open={open}
      onCancel={handleClose}
      footer={[]}
      width={500}
    >
      <Flex vertical gap="20px" align="start">
        <div style={{ flex: 1, color: '#fff', marginTop: "12px" }} className='container-fluid p-0'>
          <Form
            form={form}
            layout='vertical'
          >
            <Form.Item
              label="Reject Reason"
              name="reject_reason"
            >
              <Input.TextArea
                defaultValue={""}
                variant='filled'
                rows={4}
                maxLength={200}
                showCount={true}
              />
            </Form.Item>
          </Form>
        </div>

        <Flex justify='end' className='container-fluid p-0' style={{ marginTop: "16px" }}>
          <Space size="small">
            <Button type='default' onClick={handleClose}>
              Cancel
            </Button>
            <Button type='primary' onClick={handleConfirm}>
              Confirm
            </Button>
          </Space>
        </Flex>
      </Flex>
    </Modal>
  )
}

const ReportFieldsModal = ({ open, onClose, onConfirm, currentStep, ...props }) => {
  const [form] = useForm();

  const handleClose = () => {
    if (onClose) onClose();
  }

  const validateForm = (formData) => {
    if (!formData.incorrect_fields) {
      return false
    }
    if (!formData.report_message) {
      return false
    }
    return true;
  }

  const handleConfirm = async () => {
    const formData = form?.getFieldsValue();
    if (!validateForm(formData)) {
      message.error("Plesae fill the form accordingly.")
      return
    }

    if (onConfirm) onConfirm(formData);
  }

  const displayedFields = statusStepToDescriptionFieldsMap[currentStep];
  const fieldsOptions = displayedFields.map(
    field => ({
      value: field.name,
      label: formatField(field.name),
    })
  )

  return (
    <Modal
      {...props}
      title="Report Incorrect Fields"
      open={open}
      onCancel={handleClose}
      footer={[]}
      width={500}
    >
      <Flex vertical gap="20px" align="start">
        <div style={{ flex: 1, color: '#fff', marginTop: "12px" }} className='container-fluid p-0'>
          <Form
            form={form}
            layout='vertical'
          >
            <Form.Item
              label="Incorrect Fields"
              name="incorrect_fields"
              required
            >
              <Select
                mode='multiple'
                options={fieldsOptions}
                variant='filled'
                filterOption={(input, option) => {
                  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }}
                placeholder={'Select a field'}
              />
            </Form.Item>
            <Form.Item
              label="Report Mesasge"
              name="report_message"
              required
            >
              <Input.TextArea
                defaultValue={""}
                variant='filled'
                rows={4}
                maxLength={250}
                showCount={true}
                placeholder={"Add a message for the applicant to explain the reason for the incorrect fields"}
              />
            </Form.Item>
          </Form>
        </div>

        <Flex justify='end' className='container-fluid p-0' style={{ marginTop: "16px" }}>
          <Space size="small">
            <Button type='default' onClick={handleClose}>
              Cancel
            </Button>
            <Button type='primary' onClick={handleConfirm}>
              Confirm
            </Button>
          </Space>
        </Flex>
      </Flex>
    </Modal>
  )
}

const actionNameToModalMap = {
  'reject': RejectReasonModal,
  'report': ReportFieldsModal,
}

const StepActionModal = ({
  currentStep,
  actionConfig,
  open,
  onClose,
  onConfirm,
}) => {
  if (!actionConfig) return null;

  const Modal = actionNameToModalMap[actionConfig?.name];

  if (!Modal) return null;

  const handleCloseModal = () => {
    if (onClose) onClose();
  }

  const handleConfirmModal = (modalResult) => {
    if (onConfirm) onConfirm(modalResult);
  }

  return (
    <Modal
      open={open}
      currentStep={currentStep}
      onClose={handleCloseModal}
      onConfirm={handleConfirmModal}
      zIndex={1000}
    />
  )
}

const ActionConfirmationModal = ({ open, onClose, onConfirm, message }) => {

  const handleClose = () => {
    if (onClose) onClose();
  }

  const handleConfirm = async () => {
    if (onConfirm) await onConfirm();
  }

  return (
    <Modal
      title="Confirm Action"
      open={open}
      onCancel={handleClose}
      footer={[]}
      width={500}
      zIndex={1010}
    >
      <Flex vertical gap="20px" align="start">
        <div style={{ flex: 1, color: '#fff', marginTop: "12px" }} className='container-fluid p-0'>
          {message}
        </div>
        <Flex justify='end' className='container-fluid p-0' style={{ marginTop: "16px" }}>
          <Space size="small">
            <Button type='default' onClick={handleClose}>
              Cancel
            </Button>
            <Button type='primary' onClick={handleConfirm}>
              Confirm
            </Button>
          </Space>
        </Flex>
      </Flex>
    </Modal>
  )
}

const StepActionButtons = ({ currentStep, grant, onClick }) => {
  const [loadings, setLoadings] = useState({});

  const [selectedActionConfig, setSelectedActionConfig] = useState(null);

  const [openActionModal, setOpenActionModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const modalResultRef = useRef(null);

  const toggleLoading = (action) => {
    setLoadings((prevLoadings) => ({
      ...prevLoadings,
      [action]: !(prevLoadings[action] ?? false),
    }))
  }


  const handleOpenActionModal = () => {
    setOpenActionModal(true);
  }
  const handleCloseActionModal = () => {
    setOpenActionModal(false);

    setTimeout(() => {
      setSelectedActionConfig(null);
      modalResultRef.current = null;
    }, 300)
  }

  const handleOpenConfirmationModal = () => {
    setOpenConfirmationModal(true);
  }
  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
  }

  const handleConfirmActionModal = async (modalResult) => {
    modalResultRef.current = modalResult;
    handleOpenConfirmationModal();
  }
  const handleConfirmConfirmationModal = async () => {
    const modalResult = modalResultRef.current;
    const { name, callback } = selectedActionConfig;

    handleCloseConfirmationModal();
    handleCloseActionModal();

    toggleLoading(name);
    if (callback) await callback(grant, modalResult);
    toggleLoading(name);
    if (onClick) await onClick(name);
  }

  const actionConfigs = statusStepToActionConfigsMap[currentStep] || [];

  const getButtons = () => {
    const status = grant?.status || 'FORM_WAITING';
    const statusStep = grantStatusToStatusStepMap[status];
    const grantIsInCurrentStep = statusStep === currentStep;

    return actionConfigs.map(config => {
      const { name, type, text, callback } = config;

      const handleClick = async () => {
        setSelectedActionConfig(config);

        const hasActionModal = !!actionNameToModalMap[name];

        if (hasActionModal) {
          handleOpenActionModal();
        } else {
          handleOpenConfirmationModal();
        }
      }

      return (
        //<Button type={type} onClick={handleClick} disabled={!grantIsInCurrentStep || loadings[name]}>
        <Button type={type} onClick={handleClick} loading={loadings[name]} disabled={loadings[name]}>
          {text}
        </Button>
      )
    });
  }

  return (
    <>
      <Flex flex={1} justify="end" gap="12px">
        {getButtons()}
      </Flex>

      <ActionConfirmationModal
        open={openConfirmationModal}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmConfirmationModal}
        message={selectedActionConfig?.confirmMessage || ""}
      />

      <StepActionModal
        currentStep={currentStep}
        actionConfig={selectedActionConfig}
        open={openActionModal}
        onClose={handleCloseActionModal}
        onConfirm={handleConfirmActionModal}
      />
    </>
  )
}

const GrantDetail = () => {
  const [grantData, setGrantData] = useState({});
  const [loading, setLoading] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);

  const params = useParams();
  const history = useHistory();

  const getGrantData = async () => {
    setLoading(true);
    try {
      let result = await Grant.getById(params.id);

      const status = result?.status || 'FORM_WAITING';
      const statusStep = grantStatusToStatusStepMap[status];

      setCurrentStep(statusStep);
      setGrantData(result);
    } catch (e) {
      console.log('[GRANT DETAIL] get grant data error:', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getGrantData()
  }, [params.id])

  const handleChangeCurrentStep = (value) => {
    setCurrentStep(value);
  }

  const handleActionClick = async () => {
    history.push('/grants');
  };

  const isCompleted = currentStep === 3;

  return (
    <>
      <Container fluid>
        <Card style={{ background: Palette.BACKGROUND_DARK_GRAY, color: "white" }}
          className="card-stats mb-4 mb-xl-0">
          <CardBody>
            <Row>
              <Col className='mb-3' md={6}>
                <Space align='center'>
                  <Link to={'/grants'}>
                    <Space align='center'>
                      <Iconify icon={'material-symbols:arrow-back-rounded'} style={{ fontSize: "16px", color: "white" }}></Iconify>
                    </Space>
                  </Link>
                  <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>Grants</span>
                </Space>
              </Col>
            </Row>
            <Row>
              <Col className='mb-3' md={12} style={{ marginTop: "40px" }}>
                <Typography.Title level={3}>Grant Detail</Typography.Title>
              </Col>
            </Row>
            <Row>
              <Col>
                {loading ? (
                  <Flex justify="center" align="center">
                    <Spin />
                  </Flex>
                ) : (
                  <Flex gap={"48px"}>
                    <Flex vertical gap={"24px"} style={{ width: "100%" }}>
                      <StatusSteps
                        grant={grantData}
                        currentStep={currentStep}
                        onChangeCurrentStep={handleChangeCurrentStep}
                      />
                      {isCompleted ? (
                        <Flex vertical align="center" gap={"12px"} style={{ marginTop: '40px' }}>
                          <Iconify
                            icon={'material-symbols:check-circle-outline'}
                            style={{ fontSize: "48px", color: "white" }}
                          ></Iconify>

                          <Typography.Title level={2}>
                            Grant Completed
                          </Typography.Title>
                          <Typography.Text
                            style={{ fontSize: '1.1rem' }}
                          >
                            This grant has been completed. You can check the data for each step by pressing on the navigation item above.
                          </Typography.Text>
                            <Link to={'/grants'}>
                              <Space align='center'>
                                <Iconify icon={'material-symbols:arrow-back-rounded'} style={{ fontSize: "16px", color: "white" }}></Iconify>

                                <Typography.Text>
                                  Back to grants
                                </Typography.Text>
                              </Space>
                            </Link>
                        </Flex>
                      ) : (
                        <>
                          <StepDescriptions
                            currentStep={currentStep}
                            grant={grantData}
                          />
                          <StepActionButtons
                            currentStep={currentStep}
                            grant={grantData}
                            onClick={handleActionClick}
                          />
                        </>
                      )}
                    </Flex>
                  </Flex>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </>
  )
}

export default GrantDetail
